import Tool, { simpleToolButton } from './Tool';
import ImageSlicingBrush from '../stylus/ImageSlicingBrush';

class ToolSliceImage extends Tool {
  static toolName = 'Clip a section from image';

  // Get the color from each side of the rectangle, and return the most common
  // color. We ignore sides that are outside the image itself.
  static computeBackgroundColor(obj, ctx, x, y, width, height): string {
    const colors: Uint8ClampedArray[] = [];

    if (x > 0) {
      colors.push(ctx.getImageData(x - 1, y + height / 2, 1, 1).data);
    }
    if (x + width < obj.width) {
      colors.push(ctx.getImageData(x + width, y + height / 2, 1, 1).data);
    }
    if (y > 0) {
      colors.push(ctx.getImageData(x + width / 2, y - 1, 1, 1).data);
    }
    if (y + height < obj.height) {
      colors.push(ctx.getImageData(x + width / 2, y + height, 1, 1).data);
    }

    // Compute the mode of the colors including a fuzzy match.
    const colorCounts: Record<string, number> = {};
    colors.forEach(c => {
      // If the background is transparent at all, then completely remove the background.
      if (c[3] < 255) c[3] = 0;
      const key = `${c[0]},${c[1]},${c[2]},${c[3]}`;
      colorCounts[key] = (colorCounts[key] || 0) + 1;
    });
    const maxCount = Math.max(...Object.values(colorCounts));
    const modeColors = Object.entries(colorCounts).filter(
      ([, count]) => count === maxCount
    );

    return `rgba(${modeColors[0][0]})`;
  }

  // Remove the slice from the original image, replacing it with the background
  // color.
  static removeSlice(obj, sliceRect) {
    // Draw the image onto a canvas so we can manipulate it.
    const canvas = document.createElement('canvas');
    canvas.width = obj.width;
    canvas.height = obj.height;

    // Get the 2D context of the canvas
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;

    // Draw the image onto the canvas
    ctx.drawImage(obj.shape.image, 0, 0);

    let x, y, width, height;

    if (!obj.cropped) {
      x = (sliceRect.left - obj.left) / obj.scaleX;
      y = (sliceRect.top - obj.top) / obj.scaleY;
      width = sliceRect.width / obj.scaleX;
      height = sliceRect.height / obj.scaleY;
    } else {
      x =
        (sliceRect.left - obj.left) / obj.originalScaleX +
        obj.originalWidth * obj.croppedHorizontalOffset;
      y =
        (sliceRect.top - obj.top) / obj.originalScaleY +
        obj.originalHeight * obj.croppedVerticalOffset;
      width = sliceRect.width / obj.originalScaleX;
      height = sliceRect.height / obj.originalScaleY;
    }

    // Get the color that is "beind" the slice being removed.
    const backgroundColor = this.computeBackgroundColor(
      obj,
      ctx,
      x,
      y,
      width,
      height
    );
    if (backgroundColor.match(/,0\)/)) {
      // If the background is transparent, then we can just clear the area.
      ctx.clearRect(x, y, width, height);
    } else {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(x, y, width, height);
    }

    // Modify the original image, removing the cropped area. We make a new
    // image and upload it.
    const undoEntry = obj.controller.undoManager.currentUndoEntry();
    canvas.toBlob(blob => {
      if (!blob) return;

      obj.shape.setLocalPlaceholderImageFromBlob(blob);

      const file = new File([blob], 'image.png', { type: 'image/png' });
      obj.controller.uploadAttachment({ file }).then(attachmentId => {
        obj.controller.undoManager.addUndo(undoEntry, () => {
          obj.set({ attachmentId });
        });
      });

      canvas.remove();
    }, 'image/png');
  }

  static dataURLtoBlob(dataurl) {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  // Extract image data from the existing image shape.
  static extractImageFromSlice(obj, sliceRect) {
    const canvas = document.createElement('canvas');
    canvas.width = sliceRect.width / obj.scaleX;
    canvas.height = sliceRect.height / obj.scaleY;
    const ctx = canvas.getContext('2d')!;
    // Disable image smoothing to keep the image sharp. otherwise, the image
    // will be blurred and blurred more every slice.
    ctx.imageSmoothingEnabled = false;

    if (!obj.cropped) {
      ctx.drawImage(
        obj.shape.image,
        (sliceRect.left - obj.left) / obj.scaleX,
        (sliceRect.top - obj.top) / obj.scaleY,
        sliceRect.width / obj.scaleX,
        sliceRect.height / obj.scaleY,
        0,
        0,
        sliceRect.width / obj.scaleX,
        sliceRect.height / obj.scaleY
      );
    } else {
      ctx.drawImage(
        obj.shape.image,
        (sliceRect.left - obj.left) / obj.originalScaleX +
          obj.originalWidth * obj.croppedHorizontalOffset,
        (sliceRect.top - obj.top) / obj.originalScaleY +
          obj.originalHeight * obj.croppedVerticalOffset,
        sliceRect.width / obj.originalScaleX,
        sliceRect.height / obj.originalScaleY,
        0,
        0,
        sliceRect.width / obj.scaleX,
        sliceRect.height / obj.scaleY
      );
    }
    return canvas.toDataURL();
  }

  // Save the slice as a new image shape.
  static async commit(obj, sliceRect) {
    if (sliceRect?.width <= 0 || sliceRect?.height <= 0) return;

    const imageSrc = this.extractImageFromSlice(obj, sliceRect);
    this.removeSlice(obj, sliceRect);

    await obj.controller.actionCreateImageShape(
      imageSrc,
      {
        x: sliceRect.left,
        y: sliceRect.top,
      },
      this.dataURLtoBlob(imageSrc),
      {
        width: sliceRect.width / obj.scaleX,
        height: sliceRect.height / obj.scaleY,
        scaleX: obj.scaleX,
        scaleY: obj.scaleY,
      }
    );

    obj.controller.undoManager.completeUndoGroup();
  }

  static action({ controller, selection }) {
    const brush = new ImageSlicingBrush(controller.canvas, selection);
    controller.canvas.isDrawingMode = true;
    controller.canvas.freeDrawingBrush = brush;
  }

  static enabled({ controller, selection }) {
    return (
      super.enabled({ controller, selection }) &&
      selection &&
      (selection.angle || 0) === 0 &&
      !selection.shape.loading
    );
  }

  static buttonComponent() {
    return simpleToolButton(ToolSliceImage, 'fa-regular fa-scalpel');
  }
}

export default ToolSliceImage;
